import { TypedNodeConnection } from "./NodeConnection";
import { CoreGraphNode } from "../../../../core/graph/CoreGraphNode";
import { NodeEvent } from "../../../poly/NodeEvent";
import { ClonedStatesController } from "./utils/ClonedStatesController";
import { CoreType } from "../../../../core/Type";
import { ArrayUtils } from "../../../../core/ArrayUtils";
const MAX_INPUTS_COUNT_UNSET = 0;
export class NodeInputsController {
  constructor(node) {
    this.node = node;
    this._graphNodeInputs = [];
    this._inputs = [];
    this._has_named_inputs = false;
    this._minInputsCount = 0;
    this._maxInputsCount = MAX_INPUTS_COUNT_UNSET;
    this._maxInputsCountOnInput = MAX_INPUTS_COUNT_UNSET;
    this._depends_on_inputs = true;
  }
  dispose() {
    if (this._graphNode) {
      this._graphNode.dispose();
    }
    for (let graph_node of this._graphNodeInputs) {
      if (graph_node) {
        graph_node.dispose();
      }
    }
    this._on_update_hooks = void 0;
    this._on_update_hook_names = void 0;
  }
  setDependsOnInputs(depends_on_inputs) {
    this._depends_on_inputs = depends_on_inputs;
  }
  setMinCount(minInputsCount) {
    this._minInputsCount = minInputsCount;
  }
  minCount() {
    return this._minInputsCount;
  }
  setMaxCount(maxInputsCount) {
    if (this._maxInputsCount == MAX_INPUTS_COUNT_UNSET) {
      this._maxInputsCountOnInput = maxInputsCount;
    }
    this._maxInputsCount = maxInputsCount;
    this._initGraphNodeInputs();
  }
  namedInputConnectionPointsByName(name) {
    if (this._named_input_connection_points) {
      for (let connection_point of this._named_input_connection_points) {
        if (connection_point && connection_point.name() == name) {
          return connection_point;
        }
      }
    }
  }
  setNamedInputConnectionPoints(newConnectionPoints) {
    var _a;
    this._has_named_inputs = true;
    const connectionPointsToKeep = ((_a = this._named_input_connection_points) == null ? void 0 : _a.filter((cp) => cp == null ? void 0 : cp.inNodeDefinition())) || [];
    const allNewConnectionPoints = ArrayUtils.shallowClone(connectionPointsToKeep);
    const currentNames = /* @__PURE__ */ new Set();
    for (let connectionPointToKeep of connectionPointsToKeep) {
      if (connectionPointToKeep) {
        currentNames.add(connectionPointToKeep.name());
      }
    }
    for (let newConnectionPoint of newConnectionPoints) {
      if (newConnectionPoint) {
        if (!currentNames.has(newConnectionPoint.name())) {
          currentNames.add(newConnectionPoint.name());
          allNewConnectionPoints.push(newConnectionPoint);
        }
      }
    }
    const connections = this.node.io.connections.inputConnections();
    if (connections) {
      for (let connection of connections) {
        if (connection) {
          if (connection.input_index >= allNewConnectionPoints.length) {
            connection.disconnect({ setInput: true });
          }
        }
      }
    }
    this._named_input_connection_points = allNewConnectionPoints;
    this.setMinCount(0);
    this.setMaxCount(this._named_input_connection_points.length);
    this._initGraphNodeInputs();
    this.node.emit(NodeEvent.NAMED_INPUTS_UPDATED);
  }
  hasNamedInputs() {
    return this._has_named_inputs;
  }
  namedInputConnectionPoints() {
    return this._named_input_connection_points || [];
  }
  _initGraphNodeInputs() {
    for (let i = 0; i < this._maxInputsCount; i++) {
      this._graphNodeInputs[i] = this._graphNodeInputs[i] || this._createGraphNodeInput(i);
    }
  }
  _createGraphNodeInput(index) {
    const graph_input_node = new CoreGraphNode(this.node.scene(), `input_${index}`);
    if (!this._graphNode) {
      this._graphNode = new CoreGraphNode(this.node.scene(), "inputs");
      this.node.addGraphInput(this._graphNode, false);
    }
    this._graphNode.addGraphInput(graph_input_node, false);
    return graph_input_node;
  }
  maxInputsCount() {
    return this._maxInputsCount || 0;
  }
  maxInputsCountOverriden() {
    return this._maxInputsCount != this._maxInputsCountOnInput;
  }
  inputGraphNode(input_index) {
    return this._graphNodeInputs[input_index];
  }
  setCount(min, max) {
    if (max == null) {
      max = min;
    }
    this.setMinCount(min);
    this.setMaxCount(max);
    this._initConnectionControllerInputs();
  }
  _initConnectionControllerInputs() {
    this.node.io.connections.initInputs();
  }
  isAnyInputDirty() {
    var _a;
    return ((_a = this._graphNode) == null ? void 0 : _a.isDirty()) || false;
  }
  containersWithoutEvaluation() {
    const containers = [];
    for (let i = 0; i < this._inputs.length; i++) {
      const inputNode = this._inputs[i];
      let container = void 0;
      if (inputNode) {
        container = inputNode.containerController.containerUnlessBypassed();
      }
      containers.push(container);
    }
    return containers;
  }
  _existingInputIndices() {
    const existing_input_indices = [];
    if (this._maxInputsCount > 0) {
      for (let i = 0; i < this._inputs.length; i++) {
        if (this._inputs[i]) {
          existing_input_indices.push(i);
        }
      }
    }
    return existing_input_indices;
  }
  async evalRequiredInputs() {
    var _a;
    let containers = [];
    if (this._maxInputsCount > 0) {
      const existing_input_indices = this._existingInputIndices();
      if (existing_input_indices.length < this._minInputsCount) {
        this.node.states.error.set("inputs are missing");
      } else {
        if (existing_input_indices.length > 0) {
          const promises = [];
          let input;
          for (let i = 0; i < this._inputs.length; i++) {
            input = this._inputs[i];
            if (input) {
              promises.push(this.evalRequiredInput(i));
            }
          }
          containers = await Promise.all(promises);
          (_a = this._graphNode) == null ? void 0 : _a.removeDirtyState();
        }
      }
    }
    return containers;
  }
  async evalRequiredInput(input_index) {
    let container = void 0;
    const input_node = this.input(input_index);
    if (input_node) {
      container = await input_node.compute();
      this._graphNodeInputs[input_index].removeDirtyState();
    }
    if (container && container.coreContent()) {
    } else {
      const input_node2 = this.input(input_index);
      if (input_node2) {
        const input_error_message = input_node2.states.error.message();
        if (input_error_message) {
          this.node.states.error.set(`input ${input_index} is invalid (error: ${input_error_message})`);
        }
      }
    }
    return container;
  }
  getNamedInputIndex(name) {
    var _a;
    if (this._named_input_connection_points) {
      for (let i = 0; i < this._named_input_connection_points.length; i++) {
        if (((_a = this._named_input_connection_points[i]) == null ? void 0 : _a.name()) == name) {
          return i;
        }
      }
    }
    return -1;
  }
  getInputIndex(input_index_or_name) {
    if (CoreType.isString(input_index_or_name)) {
      if (this.hasNamedInputs()) {
        return this.getNamedInputIndex(input_index_or_name);
      } else {
        throw new Error(`node ${this.node.path()} has no named inputs`);
      }
    } else {
      return input_index_or_name;
    }
  }
  setInput(inputIndexOrName, node, outputIndexOrName, options) {
    if (outputIndexOrName == null) {
      outputIndexOrName = 0;
    }
    const noExceptionOnInvalidInput = (options == null ? void 0 : options.noExceptionOnInvalidInput) || false;
    const input_index = this.getInputIndex(inputIndexOrName) || 0;
    if (input_index < 0) {
      const message = `invalid input (${inputIndexOrName}) for node ${this.node.path()}`;
      if (!noExceptionOnInvalidInput) {
        console.warn(message);
        throw new Error(message);
      } else {
        return;
      }
    }
    let output_index = 0;
    if (node) {
      if (node.io.outputs.hasNamedOutputs()) {
        output_index = node.io.outputs.getOutputIndex(outputIndexOrName);
        if (output_index == null || output_index < 0) {
          const connection_points = node.io.outputs.namedOutputConnectionPoints();
          const names = connection_points.map((cp) => cp.name());
          console.warn(`node ${node.path()} does not have an output named ${outputIndexOrName}. inputs are: ${names.join(", ")}`);
          return;
        }
      }
    }
    const graph_input_node = this._graphNodeInputs[input_index];
    if (graph_input_node == null) {
      const message = `graph_input_node not found at index ${input_index}`;
      console.warn(message);
      throw new Error(message);
    }
    if (node && this.node.parent() != node.parent()) {
      return;
    }
    const old_input_node = this._inputs[input_index];
    let old_output_index = null;
    let old_connection = void 0;
    if (this.node.io.connections) {
      old_connection = this.node.io.connections.inputConnection(input_index);
    }
    if (old_connection) {
      old_output_index = old_connection.output_index;
    }
    if (node !== old_input_node || output_index != old_output_index) {
      if (old_input_node != null) {
        if (this._depends_on_inputs) {
          graph_input_node.removeGraphInput(old_input_node);
        }
      }
      if (node != null) {
        if (graph_input_node.addGraphInput(node)) {
          if (!this._depends_on_inputs) {
            graph_input_node.removeGraphInput(node);
          }
          if (old_connection) {
            old_connection.disconnect({ setInput: false });
          }
          this._inputs[input_index] = node;
          new TypedNodeConnection(node, this.node, output_index, input_index);
        } else {
          console.warn(`cannot connect ${node.path()} to ${this.node.path()}`);
        }
      } else {
        this._inputs[input_index] = null;
        if (old_connection) {
          old_connection.disconnect({ setInput: false });
        }
      }
      this._run_on_set_input_hooks();
      graph_input_node.setSuccessorsDirty();
      this.node.emit(NodeEvent.INPUTS_UPDATED);
    }
  }
  remove_input(node) {
    const inputs = this.inputs();
    let input;
    for (let i = 0; i < inputs.length; i++) {
      input = inputs[i];
      if (input != null && node != null) {
        if (input.graphNodeId() === node.graphNodeId()) {
          this.setInput(i, null);
        }
      }
    }
  }
  input(input_index) {
    return this._inputs[input_index];
  }
  named_input(input_name) {
    if (this.hasNamedInputs()) {
      const input_index = this.getInputIndex(input_name);
      return this._inputs[input_index];
    } else {
      return null;
    }
  }
  named_input_connection_point(input_name) {
    if (this.hasNamedInputs() && this._named_input_connection_points) {
      const input_index = this.getInputIndex(input_name);
      return this._named_input_connection_points[input_index];
    }
  }
  has_named_input(name) {
    return this.getNamedInputIndex(name) >= 0;
  }
  hasInput(input_index) {
    return this._inputs[input_index] != null;
  }
  inputs() {
    return this._inputs;
  }
  initInputsClonedState(states) {
    if (!this._clonedStatesController) {
      this._clonedStatesController = new ClonedStatesController(this);
      this._clonedStatesController.initInputsClonedState(states);
    }
  }
  overrideClonedStateAllowed() {
    var _a;
    return ((_a = this._clonedStatesController) == null ? void 0 : _a.overrideClonedStateAllowed()) || false;
  }
  overrideClonedState(state) {
    var _a;
    (_a = this._clonedStatesController) == null ? void 0 : _a.overrideClonedState(state);
  }
  clonedStateOverriden() {
    var _a;
    return ((_a = this._clonedStatesController) == null ? void 0 : _a.overriden()) || false;
  }
  cloneRequired(index) {
    var _a;
    const state = (_a = this._clonedStatesController) == null ? void 0 : _a.cloneRequiredState(index);
    if (state != null) {
      return state;
    }
    return true;
  }
  cloneRequiredStates() {
    var _a;
    const states = (_a = this._clonedStatesController) == null ? void 0 : _a.cloneRequiredStates();
    if (states != null) {
      return states;
    }
    return true;
  }
  add_on_set_input_hook(name, hook) {
    this._on_update_hooks = this._on_update_hooks || [];
    this._on_update_hook_names = this._on_update_hook_names || [];
    if (!this._on_update_hook_names.includes(name)) {
      this._on_update_hooks.push(hook);
      this._on_update_hook_names.push(name);
    } else {
      console.warn(`hook with name ${name} already exists`, this.node);
    }
  }
  _run_on_set_input_hooks() {
    if (this._on_update_hooks) {
      for (let hook of this._on_update_hooks) {
        hook();
      }
    }
  }
}
